import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { studentApi } from "../../lib/interview/api";
import LaTeXRenderer from "../../components/LaTeXRenderer";
import { getCamMicStream, getScreenStream, pickMime, ChunkedRecorder, VAD, stopAllTracks, createWatermarkedScreenStream, captureFrameFromStream } from "../../lib/interview/media";
import {
  getSessionCamStream,
  setSessionCamStream,
  getSessionWebcamRecorder,
  startSessionWebcamRecording,
  stopSessionWebcamRecording,
  releaseSessionMedia,
} from "../../lib/interview/sessionMedia";
import { startMonitor as startNetMonitor, measureOnce as measureNetOnce, suggestBitrate } from "../../lib/interview/netQuality";
import { startMicHealthMonitor } from "../../lib/interview/micHealth";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import { Logo, Overline, RecordingDot, VoiceWave } from "../../components/himt/Primitives";
import { toast } from "sonner";
import { Mic, Monitor, AlertTriangle, SkipForward, CheckCircle2, Wifi, WifiOff, Clock } from "lucide-react";

export default function LiveTest() {
  const { token } = useParams();
  const nav = useNavigate();
  const api = studentApi(token);
  const [attempt, setAttempt] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [test, setTest] = useState(null);
  const [qIndex, setQIndex] = useState(0);
  const [phase, setPhase] = useState("loading"); // loading|ready|listening|recording|saving|done
  const [ttrLeft, setTtrLeft] = useState(5);
  const [answerLeft, setAnswerLeft] = useState(30);
  const [vadActive, setVadActive] = useState(false);
  const [vadLevel, setVadLevel] = useState(0);
  const [flags, setFlags] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [testSecondsLeft, setTestSecondsLeft] = useState(null); // overall test countdown
  const [resumeIndex, setResumeIndex] = useState(0); // first unanswered question on a resumed attempt
  const [loadError, setLoadError] = useState(null);  // surfaces attempt-load failures so the page is never silently blank

  const camVideoRef = useRef(null);
  const camStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const watermarkedScreenRef = useRef(null);
  const webcamRecorderRef = useRef(null);
  const screenRecorderRef = useRef(null);
  const answerRecorderRef = useRef(null);
  const vadRef = useRef(null);
  const ttrTimerRef = useRef(null);
  const ansTimerRef = useRef(null);
  const lvlTimerRef = useRef(null); // level-poller interval — kept in a ref so
                                     // it is always cleanable across transitions.
  const answerStartRef = useRef(0);
  const currentAnswerRecorderStreamRef = useRef(null);
  const gazeTimerRef = useRef(null);
  const screenScanTimerRef = useRef(null);   // periodic AI screen-scan interval
  const displaySurfaceRef = useRef(null);     // 'monitor' | 'window' | 'browser'
  const afpStateRef = useRef({ quietSince: null });
  const recognitionRef = useRef(null);
  const transcriptRef = useRef("");
  const netMonitorRef = useRef(null);
  const netSampleRef = useRef(null);
  const poorSinceRef = useRef(null);
  const [netQuality, setNetQuality] = useState({ quality: "good", ping_ms: null, downlink_mbps: null });
  const micHealthRef = useRef(null);
  // Bluetooth earpod / USB headset users were losing their mic silently mid-test.
  // `micWarning` drives a persistent banner so they can re-select the mic from
  // the OS sound settings before continuing. Auto-clears when audio resumes.
  const [micWarning, setMicWarning] = useState(null);
  // Live chunk-queue depth (unsynced recording bytes). Displayed in the
  // HUD so low-signal candidates can see their uploads are catching up
  // instead of assuming the test is frozen.
  const [chunkQueueDepth, setChunkQueueDepth] = useState(0);

  // ---- Combined exam (MCQ + Oral) ----
  const [mcqQuestions, setMcqQuestions] = useState([]);
  const [mcqIndex, setMcqIndex] = useState(0);
  const [mcqSecondsLeft, setMcqSecondsLeft] = useState(null);
  const [mcqSubmitting, setMcqSubmitting] = useState(false);
  const mcqAnswersRef = useRef({});            // { questionId: selectedValue }
  const [mcqAnswerTick, setMcqAnswerTick] = useState(0); // forces re-render on select
  const mcqTimerRef = useRef(null);
  const mcqDoneRef = useRef(false);
  const mcqVadRef = useRef(null);          // VAD for secondary-voice detection during MCQ
  const mcqVoiceTimerRef = useRef(null);   // level poller for the MCQ voice monitor
  const mcqAfpStateRef = useRef({ quietSince: null });
  const phaseTagRef = useRef("oral");          // "mcq" | "oral" — tags proctoring flags
  const examMode = test?.exam_mode || "oral";
  const phaseOrder = test?.phase_order || "mcq_first";

  // --- Atomic state refs (updated SYNCHRONOUSLY before React state) -----
  // React's setState is async, so guarding concurrent transitions with
  // `phaseRef.current !== "listening"` wasn't enough: VAD.onActive + the TTR
  // interval could both fire beginRecordingAnswer() in the same tick, both
  // see `phase === "listening"` (phaseRef is only updated via a post-render
  // useEffect) and both start recorders. Same for finishAnswer → duplicated
  // transitions led to timers running twice (irregular countdowns),
  // questions repeating, and doubled finalize() calls breaking submission.
  //
  // These refs are mutated synchronously at the START of every transition
  // so a second concurrent call bails out immediately.
  const phaseRef = useRef("loading");
  const qIndexRef = useRef(0);
  const finishingRef = useRef(false);
  const finalizingRef = useRef(false);

  // Proctoring config — true by default if not specified
  const pc = test?.proctoring_config || {};
  const on = (k) => pc[k] !== false;

  // Load attempt
  useEffect(() => {
    (async () => {
      try {
        // We need attempt id. attempt start was called in EnvCheck but let's verify again.
        const { data: info } = await api.post("/student/verify", { token });
        let data;
        if (!info.attempt_id) {
          const { data: started } = await api.post("/student/attempt/start");
          ({ data } = await api.get(`/student/attempt/${started.id}`));
        } else {
          ({ data } = await api.get(`/student/attempt/${info.attempt_id}`));
        }
        // Guard: if ID isn't verified, don't reveal the test — bounce back to Pre-flight.
        if (data.id_verification_required) {
          toast.error("Identity not verified yet. Please complete the ID check first.", { duration: 6000 });
          window.location.href = `/exam/${token}/env`;
          return;
        }
        setAttempt(data); setTest(data.test); setQuestions(data.questions);
        mcqDoneRef.current = !!data.mcq_submitted;
        // Resume support: if the candidate previously answered N questions and
        // the tab closed / network dropped, jump straight to question N+1 when
        // they re-enter. The overall-test countdown is deadline-based off
        // attempt.started_at so elapsed time is automatically deducted.
        const ri = Number.isInteger(data.resume_index) ? data.resume_index : 0;
        if (ri > 0 && ri < (data.questions || []).length) {
          setResumeIndex(ri);
          qIndexRef.current = ri;
          setQIndex(ri);
          toast.info(`Welcome back. Resuming at question ${ri + 1} of ${data.questions.length}.`, { duration: 8000 });
        } else if (ri >= (data.questions || []).length && (data.questions || []).length > 0) {
          // All questions already answered — go straight to submit.
          toast.info("All questions answered. Submitting your test now…", { duration: 6000 });
        }
        setPhase("ready");
      } catch (e) {
        // Don't leave the candidate stranded on "Preparing your test…" forever.
        // Surface the actual failure so they can retry or contact the proctor.
        const msg = e?.response?.data?.detail || e?.message || "Failed to load your test.";
        setLoadError(msg);
        toast.error("Failed to load test: " + msg, { duration: 8000 });
      }
    })();
    // eslint-disable-next-line
  }, [token]);

  const logEvent = useCallback(async (type, severity, details) => {
    setFlags((p) => [...p, { type, severity, details, timestamp: new Date().toISOString() }]);
    try {
      if (attempt) {
        await studentApi(token).post(`/student/attempt/${attempt.id}/proctoring-event`, { type, severity, details, phase: phaseTagRef.current });
      }
    } catch (e) { /* noop */ }
  }, [attempt, token]);

  // Capture a frame of the SHARED SCREEN and send it to the backend for
  // AI-vision analysis (visible apps + screen-sharing/remote-control detection).
  // Best-effort: silently skips when no screen stream / network hiccup.
  const runScreenScan = useCallback(async () => {
    const stream = screenStreamRef.current;
    if (!stream || !attempt) return;
    try {
      const blob = await captureFrameFromStream(stream, 1280);
      if (!blob) return;
      const fd = new FormData();
      fd.append("file", new File([blob], "screen.jpg", { type: "image/jpeg" }));
      fd.append("display_surface", displaySurfaceRef.current || "");
      fd.append("phase", phaseTagRef.current || "");
      await studentApi(token).post(`/student/attempt/${attempt.id}/screen-scan`, fd, { timeout: 60_000 });
    } catch (e) { /* best-effort — never block the exam */ }
  }, [attempt, token]);

  // Fire-and-forget error reporter. Any startup / MediaRecorder failure sends
  // a full {phase, name, message, stack, browser_info} payload to the server
  // so an invigilator can see the REAL browser + error directly in the admin
  // grading view, instead of asking candidates for screenshots.
  const reportClientError = useCallback((phase, err) => {
    try {
      if (!attempt) return;
      const payload = {
        phase: String(phase || "unknown"),
        name: String(err?.name || "Error"),
        message: String(err?.message || err || ""),
        stack: String(err?.stack || "").slice(0, 4000),
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        browser_info: typeof navigator !== "undefined" ? {
          language: navigator.language,
          platform: navigator.platform,
          cookieEnabled: navigator.cookieEnabled,
          onLine: navigator.onLine,
          hwConcurrency: navigator.hardwareConcurrency,
          displaySurface: err?.surface || null,
          url: typeof location !== "undefined" ? location.href : null,
        } : {},
      };
      studentApi(token).post(`/student/attempt/${attempt.id}/client-error`, payload).catch(() => {});
    } catch (e) { /* never throw from the reporter itself */ }
  }, [attempt, token]);

  // ── GPS capture: fire once when attempt is available ────────────────────
  useEffect(() => {
    if (!attempt) return;
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        studentApi(token).post(`/student/attempt/${attempt.id}/gps`, {
          latitude:  pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy:  pos.coords.accuracy,
        }).catch(() => {});
      },
      () => { /* permission denied — silently skip */ },
      { timeout: 10000, maximumAge: 60000 },
    );
  }, [attempt?.id]);   

  // ── Overall test countdown: auto-submit when total_duration_minutes expires
  useEffect(() => {
    if (!attempt || !test) return;
    if ((test.exam_mode || "oral") === "combined") return; // combined uses per-phase timers
    const dur = test.total_duration_minutes || test.duration_minutes;
    if (!dur) return;
    const deadline = new Date(attempt.started_at).getTime() + dur * 60 * 1000;
    const tick = () => {
      const left = Math.floor((deadline - Date.now()) / 1000);
      setTestSecondsLeft(left);
      if (left <= 0 && !submitting) {
        clearInterval(id);
        toast.warning("Time is up! Auto-submitting your test…");
        setSubmitting(true);
        studentApi(token).post(`/student/attempt/${attempt.id}/submit`, {})
          .catch(() => {})
          .finally(() => navigate(`/exam/${token}/complete`));
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [attempt?.id, test?.total_duration_minutes]);  

  // Catch-all: any unhandled JS error or rejection anywhere in LiveTest gets
  // auto-reported — turns "my test wouldn't start" tickets into diagnosable
  // records in a single place.
  useEffect(() => {
    if (!attempt) return undefined;
    const onError = (ev) => reportClientError("window.onerror", ev?.error || { message: ev?.message, name: "WindowError" });
    const onRej = (ev) => reportClientError("unhandledrejection", ev?.reason || { message: "unhandled rejection" });
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRej);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRej);
    };
  }, [attempt, reportClientError]);

  // Tear down the MCQ voice monitor if the component unmounts mid-MCQ.
  useEffect(() => () => {
    try { if (mcqVoiceTimerRef.current) clearInterval(mcqVoiceTimerRef.current); } catch (e) {}
    try { if (screenScanTimerRef.current) clearInterval(screenScanTimerRef.current); } catch (e) {}
    try { mcqVadRef.current?.stop?.(); } catch (e) {}
  }, []);

  // Connection-quality monitor: measures RTT + downlink every 8s, displays a
  // live HUD pill, piggybacks the sample on each heartbeat so the admin live
  // monitor can surface shipboard Wi-Fi degradation before the candidate
  // drops. Sustained "poor" for 30s shows a one-time warning to the student.
  useEffect(() => {
    if (!attempt) return undefined;
    const mon = startNetMonitor({
      intervalMs: 8000,
      onSample: (s) => {
        netSampleRef.current = s;
        setNetQuality(s);
        // Sustained poor warning — amber toast once per 60s of continuous poor.
        if (s.quality === "poor") {
          if (!poorSinceRef.current) poorSinceRef.current = Date.now();
          else if (Date.now() - poorSinceRef.current > 30_000) {
            toast.warning(
              "Your connection is unstable. The test will continue but recordings may upload slowly.",
              { id: "poor-net", duration: 8000 }
            );
            poorSinceRef.current = Date.now() + 30_000; // re-arm only after 60s total
          }
        } else {
          poorSinceRef.current = null;
        }
        // Piggyback on heartbeat — low frequency, best-effort.
        studentApi(token)
          .post(`/student/attempt/${attempt.id}/heartbeat`, { network: s })
          .catch(() => {});
      },
    });
    netMonitorRef.current = mon;
    return () => { try { mon.stop(); } catch (_e) { /* noop */ } };
  }, [attempt, token]);

  // Mic-health monitor: catches Bluetooth earpod / USB headset failures
  // where the track silently dies or switches to a non-functional input.
  // Runs against the session cam/mic stream (same one feeding the recorder).
  useEffect(() => {
    if (!attempt) return undefined;
    const s = getSessionCamStream();
    if (!s) return undefined;
    const mon = startMicHealthMonitor(s, {
      onSilent: () => {
        setMicWarning("Your microphone is silent. If you just connected or disconnected earpods/headphones, please select the correct microphone from your OS sound settings — your voice is NOT being recorded.");
        reportClientError("mic-silent", { message: "Sustained near-zero mic RMS detected" });
      },
      onTrackEnded: () => {
        setMicWarning("Your microphone disconnected. Reconnect your earpods/headphones or switch to the built-in microphone in your OS sound settings, then click any question to continue.");
        reportClientError("mic-track-ended", { message: "Audio track ended mid-test" });
      },
      onDeviceChange: () => {
        // A device (dis)connected — just notify, don't block. The silence
        // check above will escalate to a full warning if this actually
        // broke audio capture.
        toast.info("Audio device change detected. Verify you're speaking into the correct mic.", { id: "devicechange", duration: 5000 });
      },
    });
    micHealthRef.current = mon;
    return () => { try { mon.stop(); } catch (_e) {} };
  }, [attempt, reportClientError]);

  // Lockdown: fullscreen, visibility, blur listeners — gated per proctoring_config
  useEffect(() => {
    if (!test) return;
    const handlers = [];
    const onVisibility = () => {
      if (document.hidden) logEvent("tab_switch", "high", "Tab hidden");
    };
    const onBlur = () => logEvent("window_blur", "medium", "Window blurred");
    const onContext = (e) => e.preventDefault();
    const onFsChange = () => {
      if (!document.fullscreenElement) logEvent("fullscreen_exit", "high", "Exited fullscreen");
    };

    if (on("tab_switch_detection")) {
      document.addEventListener("visibilitychange", onVisibility);
      window.addEventListener("blur", onBlur);
      handlers.push(() => {
        document.removeEventListener("visibilitychange", onVisibility);
        window.removeEventListener("blur", onBlur);
      });
    }
    if (on("fullscreen_lockdown")) {
      document.addEventListener("fullscreenchange", onFsChange);
      handlers.push(() => document.removeEventListener("fullscreenchange", onFsChange));
    }
    // Context-menu disable is always minor hygiene, not a detector
    window.addEventListener("contextmenu", onContext);
    handlers.push(() => window.removeEventListener("contextmenu", onContext));

    return () => handlers.forEach((fn) => fn());
    // eslint-disable-next-line
  }, [logEvent, test]);

  const startCapturing = async () => {
    try {
      // Reuse the stream already granted in EnvironmentCheck — no second
      // permission prompt. Fall back to a fresh getUserMedia() only if the
      // session stream is missing (e.g. browser refresh on this page).
      let cam = getSessionCamStream();
      if (!cam || cam.getVideoTracks().every((t) => t.readyState !== "live")) {
        cam = await getCamMicStream();
        setSessionCamStream(cam);
      }
      // Strict validation: must have a LIVE video track. If only audio came through
      // (user denied webcam permission or camera is in use elsewhere), fail hard.
      const videoTracks = cam.getVideoTracks();
      if (videoTracks.length === 0 || videoTracks[0].readyState !== "live") {
        cam.getTracks().forEach((t) => t.stop());
        toast.error(
          "Camera permission was denied or the webcam is not available. Please enable the camera in your browser settings and refresh — the test cannot start without video.",
          { duration: 10000 },
        );
        return;
      }
      // Watch the video track for mid-test termination
      videoTracks[0].onended = () => {
        logEvent("virtual_camera", "high", "Webcam track ended during test");
        toast.error("Webcam was disconnected — this has been flagged.", { duration: 8000 });
      };
      camStreamRef.current = cam;
      // Bind the main mini-preview immediately via a callback ref (set below
      // in the JSX). If the element is already mounted, force-assign now.
      if (camVideoRef.current && camVideoRef.current.srcObject !== cam) {
        try {
          camVideoRef.current.srcObject = cam;
          const p = camVideoRef.current.play();
          if (p && typeof p.catch === "function") p.catch(() => {});
        } catch (e) { /* noop */ }
      }

      // Fullscreen lockdown FIRST — easier for the candidate to understand
      // "the whole browser is now the exam" before the screen-share dialog.
      // Requesting fullscreen here (inside the same user gesture that started
      // the click handler) is permitted by the browser.
      if (on("fullscreen_lockdown")) {
        try { await document.documentElement.requestFullscreen(); } catch (e) { /* noop */ }
      }

      // Virtual camera heuristic — gated
      if (on("virtual_camera_detection")) {
        const vt = cam.getVideoTracks()[0];
        const lbl = (vt?.label || "").toLowerCase();
        if (lbl.includes("virtual") || lbl.includes("obs") || lbl.includes("snap") || lbl.includes("manycam") || lbl.includes("xsplit")) {
          logEvent("virtual_camera", "high", `Suspicious device: ${vt.label}`);
        }
      }

      // Screen recording — gated, with watermark overlay (Name · Student ID · IP)
      if (on("screen_recording")) {
        let scr;
        try {
          scr = await getScreenStream();
        } catch (e) {
          if (e && e.name === "ScreenSurfaceError") {
            toast.error(
              "You selected a tab or single window. HIMT requires sharing your ENTIRE SCREEN (monitor). Please click Start Examination again and choose the 'Entire Screen' option.",
              { duration: 12000 },
            );
          } else if (e && e.name === "NotAllowedError") {
            toast.error("Screen share was cancelled. The test cannot continue without full-screen sharing.", { duration: 10000 });
          } else {
            toast.error("Screen share failed. Please try again and pick 'Entire Screen'.", { duration: 10000 });
          }
          if (document.fullscreenElement) { try { await document.exitFullscreen(); } catch (err) { /* noop */ } }
          return;
        }
        screenStreamRef.current = scr;
        scr.getVideoTracks()[0].onended = () => logEvent("fullscreen_exit", "high", "Screen share stopped");

        // Any failure from THIS point on must stop the screen share so the
        // candidate isn't left with an orphaned system-wide share bar while
        // the exam hasn't actually started. Done via a local helper.
        const cleanupAfterScreen = () => {
          try { stopAllTracks(scr); } catch (e) { /* noop */ }
          screenStreamRef.current = null;
          if (document.fullscreenElement) { try { document.exitFullscreen(); } catch (e) { /* noop */ } }
        };

        // Fetch client IP for watermark (best-effort)
        let clientIp = "";
        try {
          const { data: ci } = await api.get("/student/client-info");
          clientIp = ci?.ip || "";
        } catch (e) { /* noop */ }

        let wm, scrRec;
        try {
          wm = createWatermarkedScreenStream(scr, {
            name: attempt?.student_name || "",
            id: attempt?.id?.slice(0, 8) || "",
            ip: clientIp,
          });
          watermarkedScreenRef.current = wm;
          // Monolithic screen recording: one MediaRecorder runs from the
          // start of the test until submit; at submit we upload the full
          // blob via POST /recording (no chunk / concat / finalize dance).
          // This mirrors the webcam flow and avoids the 14-30s truncation
          // bug seen when chunked uploads queued up under network stress.
          scrRec = new ChunkedRecorder(wm.stream, pickMime(true), {
            timesliceMs: 0,                    // disable chunking
            videoBitsPerSecond: 400_000,       // ~400 kbps is plenty for a
                                               // 10 fps watermarked capture
          });
          scrRec.start();
          // Surface recorder-level errors that would otherwise be silent:
          if (scrRec.recorder) {
            scrRec.recorder.onerror = (ev) => {
              console.error("[screen MediaRecorder error]", ev?.error || ev);
            };
          }
          screenRecorderRef.current = scrRec;
          // Record which surface the candidate shared (monitor / window / browser)
          // so the backend can flag a partial share, then begin the periodic
          // AI screen scan (every 2 minutes) for visible-app / screen-sharing
          // detection. First scan ~15s in once windows have settled.
          try {
            displaySurfaceRef.current = scr.getVideoTracks()[0].getSettings().displaySurface || null;
          } catch (e) { /* noop */ }
          const scanMins = Number(test?.screen_scan_interval_minutes ?? 2);
          if (scanMins > 0) {
            setTimeout(() => { runScreenScan(); }, 15_000);
            if (screenScanTimerRef.current) clearInterval(screenScanTimerRef.current);
            screenScanTimerRef.current = setInterval(() => { runScreenScan(); }, scanMins * 60_000);
          }
        } catch (e) {
          console.error("[startCapturing] screen pipeline failed:", e);
          reportClientError("screen-pipeline", e);
          cleanupAfterScreen();
          const detail = e?.message ? ` (${e.name || "Error"}: ${e.message})` : "";
          toast.error(`Could not start the screen recorder${detail}. Please refresh and try again.`, { duration: 12000 });
          return;
        }
      }

      // Webcam recording — reuse the recorder started in EnvironmentCheck so
      // the session is captured continuously from the moment the candidate
      // granted permission. Start one here only if none is already running
      // (e.g. the candidate landed directly on LiveTest after a refresh).
      if (on("webcam_recording")) {
        let sessionRec = getSessionWebcamRecorder();
        if (!sessionRec) sessionRec = startSessionWebcamRecording();
        webcamRecorderRef.current = sessionRec;
      }

      // Gaze detection via FaceDetector API if available — gated
      if (on("gaze_detection")) startGazeDetection();

      // ---- Combined exam routing ----
      const combined = (test?.exam_mode === "combined");
      const hasMcq = (attempt?.mcq_question_ids?.length || 0) > 0;
      if (combined && hasMcq && !mcqDoneRef.current && phaseOrder === "mcq_first") {
        // MCQ phase runs first under the same proctoring session.
        await enterMcqPhase();
        return;
      }

      phaseTagRef.current = "oral";
      setPhase("listening");
      // Resume-aware start: if the candidate previously answered some
      // questions on a prior session, jump straight to the first unanswered.
      // If everything was already answered, finalize immediately.
      const startIdx = Math.min(resumeIndex || 0, Math.max(0, (questions?.length || 1) - 1));
      if ((questions?.length || 0) > 0 && (resumeIndex || 0) >= questions.length) {
        // Oral done — but if combined oral-first still owes an MCQ phase, run it.
        if (combined && hasMcq && !mcqDoneRef.current && phaseOrder === "oral_first") {
          await enterMcqPhase();
          return;
        }
        finalize();
        return;
      }
      startQuestion(startIdx);
    } catch (e) {
      // Log the FULL error so we can actually diagnose mystery "permission
      // denied" reports. The candidate-facing toast now includes the error
      // name + message when available instead of a vague generic string.
      console.error("[startCapturing] fatal:", e);
      if (document.fullscreenElement) { try { await document.exitFullscreen(); } catch (err) { /* noop */ } }
      try { stopAllTracks(screenStreamRef.current); } catch (err) { /* noop */ }
      screenStreamRef.current = null;

      let msg;
      if (e && e.name === "NotAllowedError") {
        msg = "Camera/microphone permission denied. Enable both in browser settings and refresh — the test cannot start without webcam video.";
      } else if (e && e.name === "NotFoundError") {
        msg = "No camera or microphone detected on this device. Connect a webcam and retry.";
      } else if (e && e.name === "NotReadableError") {
        msg = "Your webcam is already in use by another application (Zoom, Teams, etc.). Please close it and refresh.";
      } else {
        const detail = e?.message ? ` (${e.name || "Error"}: ${e.message})` : "";
        msg = `Could not start the test${detail}. Please refresh the page and try again.`;
      }
      toast.error(msg, { duration: 12000 });
    }
  };

  const startGazeDetection = () => {
    if (typeof window === "undefined" || typeof window.FaceDetector === "undefined") {
      return; // browser doesn't support FaceDetector — silently skip
    }
    let detector;
    try {
      detector = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 3 });
    } catch (e) { return; }
    const canvas = document.createElement("canvas");
    let awaySince = null;
    let multiSince = null;
    gazeTimerRef.current = setInterval(async () => {
      try {
        const v = camVideoRef.current;
        if (!v || v.readyState < 2) return;
        canvas.width = Math.max(160, (v.videoWidth || 320) / 3);
        canvas.height = Math.max(120, (v.videoHeight || 240) / 3);
        canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
        const faces = await detector.detect(canvas);
        const now = Date.now();
        if (faces.length === 0) {
          if (!awaySince) awaySince = now;
          else if (now - awaySince > 2000) {
            logEvent("gaze_away", "medium", "Face not detected for >2s");
            awaySince = now;
          }
          multiSince = null;
        } else if (faces.length > 1) {
          if (!multiSince) multiSince = now;
          else if (now - multiSince > 1500) {
            logEvent("gaze_away", "high", `Multiple faces detected (${faces.length})`);
            multiSince = now;
          }
          awaySince = null;
        } else {
          awaySince = null;
          multiSince = null;
        }
      } catch (e) {}
    }, 1500);
  };

  // Clear ALL per-question timers + VAD + recorders. Called at the start of
  // every startQuestion so stale timers from a previous question (possibly
  // not cleaned because control left their closure early) can't leak into
  // the next one — that leak was the root cause of "irregular countdowns".
  const resetPerQuestionState = () => {
    if (ttrTimerRef.current) { clearInterval(ttrTimerRef.current); ttrTimerRef.current = null; }
    if (ansTimerRef.current) { clearInterval(ansTimerRef.current); ansTimerRef.current = null; }
    if (lvlTimerRef.current) { clearInterval(lvlTimerRef.current); lvlTimerRef.current = null; }
    try { vadRef.current?.stop(); } catch (e) { /* noop */ }
    vadRef.current = null;
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) { /* noop */ }
      recognitionRef.current = null;
    }
  };

  const startQuestion = (idx) => {
    if (idx >= questions.length) return finalize();
    // Advance the atomic index SYNCHRONOUSLY first so any in-flight
    // finishAnswer can't race us into re-entering the same question.
    qIndexRef.current = idx;
    phaseRef.current = "listening";

    resetPerQuestionState();

    setQIndex(idx);
    setPhase("listening");
    setTtrLeft(test.ttr_seconds);
    setAnswerLeft(test.answer_duration_seconds);
    setVadActive(false);

    // Heartbeat to admin live monitor
    if (attempt) {
      studentApi(token).post(`/student/attempt/${attempt.id}/heartbeat`, { current_question_index: idx }).catch(() => {});
    }

    // VAD
    const audioOnly = new MediaStream(camStreamRef.current.getAudioTracks());
    const vad = new VAD(audioOnly, {
      threshold: 0.025,
      onActive: () => {
        if (phaseRef.current === "listening") beginRecordingAnswer();
      },
    });
    vad.start();
    vadRef.current = vad;

    // Level poller (UI) + Audio fingerprinting heuristic — stored in a ref
    // so resetPerQuestionState() can always tear it down on the next
    // question, even if the TTR interval's clearInterval(lvlTick) path
    // didn't execute (e.g. VAD.onActive fired first and bypassed it).
    lvlTimerRef.current = setInterval(() => {
      if (vadRef.current) {
        const lvl = vadRef.current.level || 0;
        const active = vadRef.current.active || false;
        setVadLevel(lvl);
        setVadActive(active);

        if (on("audio_fingerprinting") && phaseRef.current === "listening") {
          if (lvl > 0.010 && lvl < 0.022) {
            if (!afpStateRef.current.quietSince) afpStateRef.current.quietSince = Date.now();
            else if (Date.now() - afpStateRef.current.quietSince > 1200) {
              logEvent("secondary_voice", "medium", `Background voice detected (rms=${lvl.toFixed(3)})`);
              afpStateRef.current.quietSince = Date.now();
            }
          } else {
            afpStateRef.current.quietSince = null;
          }
        }
      }
    }, 150);

    // TTR countdown
    let left = test.ttr_seconds;
    ttrTimerRef.current = setInterval(() => {
      left -= 1;
      setTtrLeft(left);
      if (vadRef.current?.active && phaseRef.current === "listening") {
        beginRecordingAnswer();
        return;
      }
      if (left <= 0) {
        if (ttrTimerRef.current) { clearInterval(ttrTimerRef.current); ttrTimerRef.current = null; }
        skipQuestion("No voice detected within TTR");
      }
    }, 1000);
  };

  // Keep phaseRef synced with React state for any consumers that read it
  // AFTER we've already handed control back to React (e.g. gaze detector).
  // Transition-critical code mutates phaseRef.current synchronously BEFORE
  // calling setPhase — see beginRecordingAnswer / finishAnswer below.
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // Attach webcam stream to the mini-preview <video> element whenever both
  // the ref (rendered only after leaving the "ready" phase) and the stream
  // become available. Without this effect, the preview stays black because
  // the ref is null at the moment getUserMedia resolves.
  useEffect(() => {
    if (phase === "loading" || phase === "ready") return;
    if (camVideoRef.current && camStreamRef.current) {
      try {
        if (camVideoRef.current.srcObject !== camStreamRef.current) {
          camVideoRef.current.srcObject = camStreamRef.current;
        }
        const p = camVideoRef.current.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } catch (e) { /* noop */ }
    }
  }, [phase]);

  const beginRecordingAnswer = () => {
    // Synchronous guard: flip phaseRef BEFORE any async work so a second
    // concurrent call (e.g. VAD.onActive + TTR interval tick in the same
    // frame) bails out immediately. React's phase state may still be
    // "listening" until the next render, but phaseRef is authoritative.
    if (phaseRef.current !== "listening") return;
    phaseRef.current = "recording";
    if (ttrTimerRef.current) { clearInterval(ttrTimerRef.current); ttrTimerRef.current = null; }
    setPhase("recording");

    // New recorder for answer audio only
    const audioOnly = new MediaStream(camStreamRef.current.getAudioTracks());
    currentAnswerRecorderStreamRef.current = audioOnly;
    const rec = new ChunkedRecorder(audioOnly, pickMime(false));
    rec.start();
    answerRecorderRef.current = rec;
    answerStartRef.current = Date.now();

    // Free in-browser speech-to-text via Web Speech API (Chromium-native).
    // Runs in parallel with audio recording — instant transcript, zero server cost.
    transcriptRef.current = "";
    const SpeechRec = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRec) {
      try {
        // Chrome auto-terminates SpeechRecognition after ~60s of silence and
        // occasionally mid-stream. Auto-restart as long as we're still in the
        // "recording" phase so we don't lose the candidate's voice.
        const createAndStart = () => {
          const r = new SpeechRec();
          r.lang = "en-IN";
          r.continuous = true;
          r.interimResults = true;
          r.onresult = (ev) => {
            let finalText = "";
            for (let i = ev.resultIndex; i < ev.results.length; i++) {
              if (ev.results[i].isFinal) finalText += ev.results[i][0].transcript + " ";
            }
            // Append only finalised chunks to the transcript ref (sent to
            // backend on finishAnswer). Interim / partial results are
            // intentionally NOT stored or displayed — students found the
            // live transcript distracting.
            if (finalText) transcriptRef.current += finalText;
          };
          r.onerror = (e) => {
            // "no-speech" and "aborted" are benign — let onend handle restart.
            if (e && e.error && e.error !== "no-speech" && e.error !== "aborted") {
              console.warn("SpeechRec error:", e.error);
            }
          };
          r.onend = () => {
            // If we're still recording this answer, immediately restart the
            // recognizer — Chrome kills it after ~60s of silence.
            if (phaseRef.current === "recording" && recognitionRef.current === r) {
              try { createAndStart(); } catch (e) { /* noop */ }
            }
          };
          r.start();
          recognitionRef.current = r;
        };
        createAndStart();
      } catch (e) { /* ignore */ }
    }

    // Answer countdown
    let left = test.answer_duration_seconds;
    setAnswerLeft(left);
    ansTimerRef.current = setInterval(() => {
      left -= 1;
      setAnswerLeft(left);
      if (left <= 0) {
        clearInterval(ansTimerRef.current);
        finishAnswer(false);
      }
    }, 1000);
  };

  const finishAnswer = async (skipped = false) => {
    // Synchronous guard — a second concurrent call (e.g. ansTimer hitting 0
    // the same frame the candidate tapped Finish Answer, or two rapid
    // finishAnswer calls from earlier double-fires) must be a no-op. Without
    // this, qIndex advanced twice → questions appeared to repeat / skip, and
    // on the last question finalize() ran twice and broke submission.
    if (finishingRef.current) return;
    finishingRef.current = true;
    phaseRef.current = "saving";

    if (ansTimerRef.current) { clearInterval(ansTimerRef.current); ansTimerRef.current = null; }
    if (ttrTimerRef.current) { clearInterval(ttrTimerRef.current); ttrTimerRef.current = null; }
    if (lvlTimerRef.current) { clearInterval(lvlTimerRef.current); lvlTimerRef.current = null; }
    try { vadRef.current?.stop(); } catch (e) { /* noop */ }

    // Stop browser STT and capture final transcript
    let clientTranscript = "";
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) {}
      recognitionRef.current = null;
      clientTranscript = (transcriptRef.current || "").trim();
    }

    setPhase("saving");
    // Read question via the atomic qIndexRef — not React state — so a stale
    // render doesn't hand us the WRONG question after multiple transitions.
    const currentIdx = qIndexRef.current;
    const question = questions[currentIdx];
    const duration = (Date.now() - (answerStartRef.current || Date.now())) / 1000;

    let blob = null;
    if (answerRecorderRef.current) {
      blob = await answerRecorderRef.current.stop();
      answerRecorderRef.current = null;
    }

    try {
      const fd = new FormData();
      fd.append("question_id", question.id);
      fd.append("skipped", String(skipped));
      fd.append("duration_seconds", String(duration));
      if (!skipped && clientTranscript) {
        fd.append("client_transcription", clientTranscript);
      }
      if (!skipped && blob) {
        fd.append("file", new File([blob], "answer.webm", { type: blob.type || "audio/webm" }));
      }
      // Retry with exp-backoff — we MUST persist the candidate's spoken
      // answer even if the network hiccups mid-request. 5 attempts,
      // 800ms → 12.8s backoff; 30s timeout per attempt.
      let lastErr = null;
      for (let i = 1; i <= 5; i++) {
        try {
          await api.post(`/student/attempt/${attempt.id}/answer`, fd, { timeout: 30_000 });
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
          if (i < 5) await new Promise((r) => setTimeout(r, 800 * Math.pow(2, i - 1)));
        }
      }
      if (lastErr) {
        console.warn("[answer-upload] failed after 5 retries", lastErr);
        toast.error("Your answer did not fully upload. Continuing — let the invigilator know.", { duration: 6000 });
      }
    } catch (e) {
      toast.error("Failed to save answer (continuing)");
    }

    // Atomically advance to the next question. Release the guard right
    // before handing control off so a retry on a transient error can still
    // finish this answer (but a duplicate fire of the SAME answer cannot).
    const next = currentIdx + 1;
    finishingRef.current = false;
    if (next >= questions.length) {
      // Oral phase finished. Combined oral-first still owes the MCQ phase.
      if (test?.exam_mode === "combined" && (attempt?.mcq_question_ids?.length || 0) > 0
          && !mcqDoneRef.current && phaseOrder === "oral_first") {
        enterMcqPhase();
      } else {
        finalize();
      }
    } else startQuestion(next);
  };

  const skipQuestion = (reason) => {
    logEvent("gaze_away", "low", reason || "skipped");
    finishAnswer(true);
  };

  // ---- Combined exam: MCQ phase ----
  const proceedAfterMcq = () => {
    // After MCQ submission, route to the remaining phase.
    if (phaseOrder === "mcq_first") {
      phaseTagRef.current = "oral";
      const startIdx = Math.min(resumeIndex || 0, Math.max(0, (questions?.length || 1) - 1));
      if ((questions?.length || 0) > 0 && (resumeIndex || 0) >= questions.length) {
        finalize();
        return;
      }
      setPhase("listening");
      startQuestion(startIdx);
    } else {
      // oral_first → oral already done, MCQ was the last phase.
      finalize();
    }
  };

  const stopMcqVoiceMonitor = () => {
    try { if (mcqVoiceTimerRef.current) { clearInterval(mcqVoiceTimerRef.current); mcqVoiceTimerRef.current = null; } } catch (e) {}
    try { if (mcqVadRef.current) { mcqVadRef.current.stop?.(); mcqVadRef.current = null; } } catch (e) {}
    mcqAfpStateRef.current = { quietSince: null };
  };

  const startMcqVoiceMonitor = () => {
    // Continuous secondary-voice detection during the MCQ phase. Mirrors the
    // oral-phase heuristic and is gated on the same proctoring toggle. Each
    // sustained background-voice burst logs a `secondary_voice` flag tagged
    // phase="mcq"; the ranksheet deducts 0.2 marks per such flag.
    if (!on("audio_fingerprinting")) return;
    if (!camStreamRef.current) return;
    try {
      const audioOnly = new MediaStream(camStreamRef.current.getAudioTracks());
      const vad = new VAD(audioOnly, { threshold: 0.025, onActive: () => {} });
      vad.start();
      mcqVadRef.current = vad;
      mcqVoiceTimerRef.current = setInterval(() => {
        if (phaseRef.current !== "mcq" || !mcqVadRef.current) return;
        const lvl = mcqVadRef.current.level || 0;
        // Background-voice band: a second person talking at a distance sits
        // in this RMS window (the test-taker is silent while reading MCQs).
        if (lvl > 0.010 && lvl < 0.022) {
          if (!mcqAfpStateRef.current.quietSince) mcqAfpStateRef.current.quietSince = Date.now();
          else if (Date.now() - mcqAfpStateRef.current.quietSince > 1200) {
            logEvent("secondary_voice", "medium", `Background voice during MCQ (rms=${lvl.toFixed(3)})`);
            mcqAfpStateRef.current.quietSince = Date.now();
          }
        } else {
          mcqAfpStateRef.current.quietSince = null;
        }
      }, 150);
    } catch (e) { /* noop */ }
  };

  const enterMcqPhase = async () => {
    phaseTagRef.current = "mcq";
    resetPerQuestionState();
    try {
      const { data } = await api.get(`/student/attempt/${attempt.id}/mcq-questions`);
      if (data.submitted) {
        mcqDoneRef.current = true;
        proceedAfterMcq();
        return;
      }
      const qs = data.questions || [];
      if (qs.length === 0) {
        // Nothing to ask — skip MCQ gracefully.
        mcqDoneRef.current = true;
        proceedAfterMcq();
        return;
      }
      setMcqQuestions(qs);
      setMcqIndex(0);
      mcqAnswersRef.current = {};
      setPhase("mcq");
      phaseRef.current = "mcq";

      // MCQ countdown — deadline-based so it survives re-renders.
      const mins = Number(test?.mcq_duration_minutes) || 15;
      const deadline = Date.now() + mins * 60 * 1000;
      if (mcqTimerRef.current) clearInterval(mcqTimerRef.current);
      const tick = () => {
        const left = Math.floor((deadline - Date.now()) / 1000);
        setMcqSecondsLeft(left);
        if (left <= 0) {
          clearInterval(mcqTimerRef.current);
          mcqTimerRef.current = null;
          toast.warning("MCQ time is up — submitting this section.");
          submitMcqPhase(true);
        }
      };
      tick();
      mcqTimerRef.current = setInterval(tick, 1000);
      startMcqVoiceMonitor();
    } catch (e) {
      toast.error("Could not load the MCQ section. Continuing to the next phase.", { duration: 8000 });
      mcqDoneRef.current = true;
      proceedAfterMcq();
    }
  };

  const selectMcqAnswer = (qid, value, multiple = false) => {
    if (multiple) {
      const cur = Array.isArray(mcqAnswersRef.current[qid]) ? mcqAnswersRef.current[qid] : [];
      const set = new Set(cur);
      set.has(value) ? set.delete(value) : set.add(value);
      mcqAnswersRef.current[qid] = Array.from(set);
    } else {
      mcqAnswersRef.current[qid] = value;
    }
    setMcqAnswerTick((t) => t + 1);
  };

  const submitMcqPhase = async (auto = false) => {
    if (mcqDoneRef.current || mcqSubmitting) return;
    setMcqSubmitting(true);
    stopMcqVoiceMonitor();
    if (mcqTimerRef.current) { clearInterval(mcqTimerRef.current); mcqTimerRef.current = null; }
    try {
      let ok = false;
      for (let i = 1; i <= 4; i++) {
        try {
          await api.post(`/student/attempt/${attempt.id}/mcq-submit`, { answers: mcqAnswersRef.current });
          ok = true; break;
        } catch (e) {
          if (i < 4) await new Promise((r) => setTimeout(r, 800 * i));
        }
      }
      if (!ok) toast.error("MCQ answers may not have fully saved. Continuing.", { duration: 6000 });
      mcqDoneRef.current = true;
      setMcqSubmitting(false);
      proceedAfterMcq();
    } catch (e) {
      mcqDoneRef.current = true;
      setMcqSubmitting(false);
      proceedAfterMcq();
    }
  };

  const finalize = async () => {
    // Guard against concurrent finalize() calls — double-fires of finishAnswer
    // on the last question used to land here twice, stopping the session
    // recorder the second time yielded null, and the UI was stranded in
    // submit_failed with no recoverable state.
    if (finalizingRef.current) return;
    finalizingRef.current = true;
    setSubmitting(true);
    setPhase("saving");
    phaseRef.current = "saving";

    try {
      // 1) Stop BOTH recorders and get the full monolithic blobs. These are
      //    the authoritative session recordings — one blob each, captured
      //    from the moment the candidate granted camera/mic permission on
      //    Environment Check right up to this instant.
      toast.loading("Finalising session recording…", { id: "submit" });
      let webcamBlob = null;
      let screenBlob = null;
      if (webcamRecorderRef.current) {
        try { webcamBlob = await stopSessionWebcamRecording(); } catch (_e) { /* noop */ }
        webcamRecorderRef.current = null;
      }
      if (screenRecorderRef.current) {
        try { screenBlob = await screenRecorderRef.current.stop(); } catch (_e) { /* noop */ }
      }

      // 2) Upload each blob monolithically with retries. Total upload size
      //    is bounded (~60 MB webcam @ 30 min / 250 kbps; ~150 MB screen)
      //    so a single POST is both simpler and more reliable than chunking.
      const uploadKind = async (kind, blob) => {
        if (!blob || blob.size < 100) return;
        const fd = new FormData();
        fd.append("kind", kind);
        fd.append("file", new File([blob], `${kind}.webm`, { type: blob.type || "video/webm" }));
        // Up to 5 attempts with exp-backoff; 2-minute timeout per attempt
        // so a 60 MB upload at 500 kbps (~16 min) is accepted at the very
        // edge — in practice our LAN speed tests show <30s for 60 MB.
        let lastErr = null;
        for (let i = 1; i <= 5; i++) {
          try {
            await api.post(`/student/attempt/${attempt.id}/recording`, fd, {
              timeout: 240_000,
              onUploadProgress: (ev) => {
                if (!ev.total) return;
                const pct = Math.min(100, Math.round((ev.loaded / ev.total) * 100));
                toast.loading(`Uploading ${kind} recording… ${pct}%`, { id: "submit" });
              },
            });
            return;
          } catch (e) {
            lastErr = e;
            if (i < 5) await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, i - 1)));
          }
        }
        console.warn(`[submit] ${kind} upload failed after 5 retries`, lastErr);
      };
      toast.loading("Uploading recordings…", { id: "submit" });
      await Promise.all([uploadKind("webcam", webcamBlob), uploadKind("screen", screenBlob)]);

      // 4) Submit the attempt itself — retry 3× on network blips.
      toast.loading("Submitting your answers…", { id: "submit" });
      let submitOk = false;
      let submitErr = null;
      for (let i = 1; i <= 3; i++) {
        try {
          await api.post(`/student/attempt/${attempt.id}/submit`);
          submitOk = true;
          break;
        } catch (e) {
          submitErr = e;
          if (i < 3) await new Promise((r) => setTimeout(r, 1000 * i));
        }
      }
      if (!submitOk) throw submitErr || new Error("Submission failed");

      toast.success("Submitted successfully.", { id: "submit" });
      setPhase("done");

      // Fully release the session webcam + mic now that everything is uploaded.
      releaseSessionMedia();
      camStreamRef.current = null;
      if (screenScanTimerRef.current) { clearInterval(screenScanTimerRef.current); screenScanTimerRef.current = null; }
      stopAllTracks(screenStreamRef.current);
      if (watermarkedScreenRef.current) {
        try { watermarkedScreenRef.current.stop(); } catch (e) {}
      }
      if (document.fullscreenElement) { try { document.exitFullscreen(); } catch (e) {} }
      nav(`/exam/${token}/complete`);
    } catch (e) {
      toast.error(
        "Submission failed due to a network issue. Please check your connection and tap Retry — do NOT close this tab.",
        { id: "submit", duration: 12000 },
      );
      setPhase("submit_failed");
      phaseRef.current = "submit_failed";
      // Release the finalize guard so the Retry button can re-enter.
      finalizingRef.current = false;
    } finally { setSubmitting(false); }
  };

  // Retry entry point shown in the "submit_failed" UI below.
  const retrySubmit = async () => {
    if (finalizingRef.current) return;
    finalizingRef.current = true;
    setPhase("saving");
    phaseRef.current = "saving";
    try {
      toast.loading("Retrying submission…", { id: "submit" });
      await api.post(`/student/attempt/${attempt.id}/submit`);
      toast.success("Submitted successfully.", { id: "submit" });
      releaseSessionMedia();
      camStreamRef.current = null;
      if (screenScanTimerRef.current) { clearInterval(screenScanTimerRef.current); screenScanTimerRef.current = null; }
      stopAllTracks(screenStreamRef.current);
      if (document.fullscreenElement) { try { document.exitFullscreen(); } catch (e) {} }
      nav(`/exam/${token}/complete`);
    } catch (e) {
      toast.error("Still failing. Please check your internet connection and try once more.", { id: "submit", duration: 10000 });
      setPhase("submit_failed");
      phaseRef.current = "submit_failed";
      finalizingRef.current = false;
    }
  };

  if (phase === "loading" || !test) {
    if (loadError) {
      return (
        <div className="min-h-screen grid place-items-center bg-white px-6 py-10" data-testid="live-load-error">
          <div className="max-w-md w-full text-center border-2 border-red-300 bg-red-50 rounded-sm p-8">
            <AlertTriangle size={40} className="mx-auto text-red-600" />
            <div className="font-display font-bold text-[#0A2540] text-2xl mt-4">
              Couldn't load your test
            </div>
            <p className="text-sm text-slate-700 mt-3 leading-relaxed font-mono">
              {loadError}
            </p>
            <p className="text-xs text-slate-500 mt-3 leading-relaxed">
              If the problem persists, please ask the proctor to <b>Reset & Resume</b> your attempt from the admin dashboard, then re-open this same invite link.
            </p>
            <Button
              onClick={() => window.location.reload()}
              className="mt-6 bg-[#0A2540] text-white rounded-sm px-6 py-4 text-sm font-bold"
              data-testid="live-load-retry-btn"
            >
              Retry
            </Button>
          </div>
        </div>
      );
    }
    return <div className="min-h-screen grid place-items-center text-slate-500" data-testid="live-loading">Preparing your test…</div>;
  }

  if (phase === "ready") {
    const isMacOs = /Mac|iPhone|iPad/.test(navigator.platform || "");
    const isResume = (resumeIndex || 0) > 0;
    return (
      <div className="min-h-screen bg-white grid place-items-center px-6 py-10">
        <div className="max-w-2xl w-full">
          <div className="text-center">
            <Logo className="justify-center inline-flex" />
            <h1 className="font-display font-black tracking-tighter text-[#0A2540] text-4xl mt-8">
              {isResume ? "Welcome back. Resume your exam." : "You're about to enter the exam."}
            </h1>
            <p className="text-slate-600 mt-3">
              {isResume
                ? <>You previously answered <b>{resumeIndex} of {questions.length}</b> questions. Tap Resume Examination to continue with question <b>{resumeIndex + 1}</b>. Your remaining time is unchanged.</>
                : <>Follow the steps below in order. A <b>full-screen share dialog will appear</b> after you tap Start Examination.</>}
            </p>
          </div>

          {isResume && (
            <div
              className="mt-6 p-5 bg-amber-50 border-2 border-amber-300 rounded-sm"
              data-testid="resume-banner"
            >
              <div className="text-[10px] font-mono font-bold tracking-[0.2em] uppercase text-amber-800">
                Resume in progress
              </div>
              <div className="mt-2 text-sm text-slate-800 leading-relaxed">
                Already answered: <b className="font-mono">{resumeIndex}</b> · Remaining: <b className="font-mono">{questions.length - resumeIndex}</b>. Your previously submitted answers were preserved. The overall test timer continues from where you left off.
              </div>
            </div>
          )}

          {/* What to expect — TTR + question-count reminder. Students
              reported they were unclear what to do when a question
              appeared ("do I type? do I speak? how long?"). This makes
              the rules concrete BEFORE they commit. */}
          <div
            className="mt-6 p-5 bg-[#0A2540] text-white rounded-sm"
            data-testid="what-to-expect-card"
          >
            <div className="text-[10px] font-mono font-bold tracking-[0.2em] uppercase text-sky-300">
              What to expect
            </div>
            <div className="mt-3 grid sm:grid-cols-2 gap-4">
              <div>
                <div className="font-mono font-black text-4xl tracking-tight text-sky-300">
                  {test?.ttr_seconds ?? 5}s
                </div>
                <div className="text-xs font-mono mt-1 text-slate-300 leading-relaxed">
                  Response time per question. <b className="text-white">Start speaking within {test?.ttr_seconds ?? 5} seconds</b> of each question appearing, or it auto-skips.
                </div>
              </div>
              <div>
                <div className="font-mono font-black text-4xl tracking-tight text-sky-300">
                  {questions.length || "—"}
                </div>
                <div className="text-xs font-mono mt-1 text-slate-300 leading-relaxed">
                  Questions total. Answer them one at a time — the next question loads automatically when you finish speaking.
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-white/10 text-xs font-mono text-slate-300">
              <b className="text-amber-300">Answer out loud.</b> There is no typing. Speak clearly into your microphone; your voice is transcribed live and graded.
            </div>
          </div>

          {/* macOS callout — reminds candidates they need System Settings →
              Screen Recording permission granted + Chrome restarted. Shown
              only when the OS is macOS. */}
          {examMode === "combined" && (
            <div className="mt-6 p-4 bg-[#2563EB]/5 border-l-4 border-[#2563EB] rounded-sm text-sm text-[#0A2540]" data-testid="combined-notice">
              <b>This is a combined exam.</b> It has a written <b>MCQ section</b> and a spoken <b>Oral section</b>.{" "}
              {phaseOrder === "mcq_first"
                ? "You'll complete the MCQs first, then the oral questions."
                : "You'll complete the oral questions first, then the MCQs."}{" "}
              Both sections are proctored continuously — your camera, screen and microphone stay recorded throughout.
            </div>
          )}

          {isMacOs && on("screen_recording") && (
            <div
              className="mt-6 p-4 bg-amber-50 border-l-4 border-amber-400 rounded-sm text-sm text-amber-900"
              data-testid="macos-reminder"
            >
              <b>macOS candidates:</b> if your <b>Share</b> button stays grey, macOS needs Chrome to have <b>Screen Recording</b> permission. Open <b>System Settings → Privacy &amp; Security → Screen Recording</b> and enable Google Chrome — then <b>quit &amp; reopen Chrome</b> (the toggle only takes effect after a full restart). Also disconnect Sidecar / AirPlay / mirrored displays first.
            </div>
          )}

          {/* Step-by-step enter-exam flow with arrow markers — addresses the
              Windows-Chrome confusion where candidates didn't click the
              preview + Share button in the browser's native picker. */}
          <ol className="mt-8 space-y-3" data-testid="enter-exam-steps">
            <StartStep n="1" text={<>Tap <b>Start Examination</b> below. Your browser locks into full-screen.</>} />
            {on("screen_recording") && (
              <>
                <StartStep n="2" text={<>A grey dialog opens: <b>"Choose what to share"</b>. Click the <b>"Entire screen"</b> tab at the top.</>} />
                <StartStep n="3" text={<>Click the <b>thumbnail preview</b> of your screen — it will highlight in blue.</>} />
                <StartStep n="4" text={<>Click the blue <b>"Share"</b> button at the bottom-right. <span className="text-red-600 font-bold">If you don't click the preview first, "Share" stays grey.</span></>} />
              </>
            )}
          </ol>

          <div className="mt-8 text-center">
            <Button
              onClick={startCapturing}
              className="bg-[#0A2540] text-white rounded-sm px-10 py-6 text-base font-bold"
              data-testid="start-capture-btn"
            >
              <Mic size={18} className="mr-2" /> {isResume ? "Resume Examination" : "Start Examination"}
            </Button>
            <div className="mt-4 text-xs text-slate-500 font-mono">
              Recording: session webcam + mic is already active since pre-test.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- Combined exam: MCQ phase UI (one question per page) ----
  if (phase === "mcq") {
    const mq = mcqQuestions[mcqIndex] || {};
    const choices = mq.choices || [];
    const qtype = mq.question_type;
    const isMultiple = qtype === "mcq_multiple";
    const cur = mcqAnswersRef.current[mq.id];
    const isLast = mcqIndex >= mcqQuestions.length - 1;
    const answeredCount = mcqQuestions.filter((q) => {
      const v = mcqAnswersRef.current[q.id];
      return v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0);
    }).length;
    const secs = mcqSecondsLeft;
    return (
      <div className="min-h-screen bg-white select-none" style={{ userSelect: "none" }} data-testid="mcq-phase-root">
        <header className="fixed top-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200">
          <div className="max-w-[1400px] mx-auto px-6 h-14 flex items-center justify-between">
            <div className="flex items-center gap-6">
              <Logo />
              <Overline>MCQ Section</Overline>
            </div>
            <div className="flex items-center gap-5">
              <RecordingDot />
              <div className="flex items-center gap-2 text-xs font-mono text-slate-500"><Monitor size={14} /> Screen</div>
              <div className="flex items-center gap-2 text-xs font-mono text-slate-500"><Mic size={14} /> Mic</div>
              <NetQualityPill q={netQuality} />
              {secs !== null && (
                <div
                  data-testid="mcq-countdown-pill"
                  className={`flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[11px] font-mono font-black ${
                    secs <= 60 ? "bg-red-50 border-red-400 text-red-700 animate-pulse"
                    : secs <= 180 ? "bg-amber-50 border-amber-400 text-amber-700"
                    : "bg-slate-50 border-slate-300 text-slate-600"
                  }`}
                  title="Remaining time for the MCQ section"
                >
                  <Clock size={11} />
                  {secs > 0 ? `${String(Math.floor(secs / 60)).padStart(2,"0")}:${String(secs % 60).padStart(2,"0")} left` : "Time up"}
                </div>
              )}
              <div className="text-xs font-mono text-slate-500">Q {mcqIndex + 1}/{mcqQuestions.length}</div>
            </div>
          </div>
          <Progress value={(mcqIndex / Math.max(1, mcqQuestions.length)) * 100} className="h-0.5 rounded-none" />
        </header>

        {/* Webcam preview keeps gaze detection alive during MCQ */}
        <div className="fixed top-20 right-6 z-30 w-44 h-28 bg-black rounded-sm border border-slate-200 overflow-hidden shadow-lg">
          <video
            ref={(el) => {
              camVideoRef.current = el;
              if (el && camStreamRef.current && el.srcObject !== camStreamRef.current) {
                try { el.srcObject = camStreamRef.current; const p = el.play(); if (p?.catch) p.catch(() => {}); } catch (e) {}
              }
            }}
            muted playsInline autoPlay className="w-full h-full object-cover" data-testid="mcq-webcam-preview"
          />
          <div className="absolute top-1 left-1"><span className="h-2 w-2 rounded-full bg-red-500 recording-pulse inline-block" /></div>
        </div>

        <main className="pt-28 pb-32 max-w-3xl mx-auto px-6 lg:px-10">
          <Overline>Question {String(mcqIndex + 1).padStart(2, "0")} of {String(mcqQuestions.length).padStart(2, "0")}</Overline>
          <h1 className="font-display font-bold tracking-tight text-[#0A2540] text-2xl sm:text-3xl mt-5 leading-snug" data-testid="mcq-question-text">
            <LaTeXRenderer text={mq.question_text} />
          </h1>
          {mq.media_url && mq.media_type === "image" && (
            <img src={mq.media_url} alt="" className="mt-5 max-h-64 rounded-sm border border-slate-200" />
          )}

          <div className="mt-8 space-y-3" data-testid="mcq-choices">
            {choices.map((c, i) => {
              const selected = isMultiple ? (Array.isArray(cur) && cur.includes(i)) : cur === i;
              return (
                <button
                  type="button"
                  key={i}
                  onClick={() => selectMcqAnswer(mq.id, i, isMultiple)}
                  className={`w-full text-left flex items-center gap-3 p-4 rounded-sm border-2 transition-colors ${
                    selected ? "border-[#2563EB] bg-[#2563EB]/5" : "border-slate-200 hover:border-slate-300 bg-white"
                  }`}
                  data-testid={`mcq-choice-${i}`}
                >
                  <span className={`h-6 w-6 shrink-0 flex items-center justify-center font-mono text-xs font-bold rounded-sm border ${
                    selected ? "bg-[#2563EB] text-white border-[#2563EB]" : "border-slate-300 text-slate-500"
                  }`}>
                    {String.fromCharCode(65 + i)}
                  </span>
                  <span className="text-sm text-[#0A2540]"><LaTeXRenderer text={String(c)} /></span>
                </button>
              );
            })}
          </div>

          <div className="mt-10 flex items-center justify-between">
            <Button
              variant="outline"
              disabled={mcqIndex === 0}
              onClick={() => setMcqIndex((i) => Math.max(0, i - 1))}
              className="rounded-sm border-[#0A2540] text-[#0A2540]"
              data-testid="mcq-prev-btn"
            >Previous</Button>
            <div className="text-xs font-mono text-slate-500">Answered {answeredCount}/{mcqQuestions.length}</div>
            {isLast ? (
              <Button
                onClick={() => submitMcqPhase(false)}
                disabled={mcqSubmitting}
                className="bg-[#0A2540] text-white rounded-sm px-8"
                data-testid="mcq-submit-btn"
              >
                {mcqSubmitting ? "Submitting…" : "Submit & Continue"}
              </Button>
            ) : (
              <Button
                onClick={() => setMcqIndex((i) => Math.min(mcqQuestions.length - 1, i + 1))}
                className="bg-[#2563EB] text-white rounded-sm px-8"
                data-testid="mcq-next-btn"
              >Next</Button>
            )}
          </div>
          <p className="mt-4 text-[11px] font-mono text-slate-400 text-center">
            {phaseOrder === "mcq_first" ? "After submitting, you'll proceed to the spoken (oral) section." : "This is the final section. Submitting will finish your exam."}
          </p>
        </main>

        {flags.length > 0 && (
          <footer className="fixed bottom-0 inset-x-0 bg-red-50 border-t border-red-200 z-20">
            <div className="max-w-[1400px] mx-auto px-6 py-2 flex items-center gap-3 text-xs">
              <AlertTriangle size={14} className="text-red-600" />
              <span className="font-mono font-bold text-red-700">{flags.length} proctoring flag(s)</span>
            </div>
          </footer>
        )}
      </div>
    );
  }

  const question = questions[qIndex];
  const progress = ((qIndex) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-white select-none" style={{ userSelect: "none" }} data-testid="live-test-root">
      {/* Fixed header */}
      <header className="fixed top-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Logo />
            <Overline>Live Examination</Overline>
          </div>
          <div className="flex items-center gap-5">
            <RecordingDot />
            <div className="flex items-center gap-2 text-xs font-mono text-slate-500">
              <Monitor size={14} /> Screen
            </div>
            <div className="flex items-center gap-2 text-xs font-mono text-slate-500">
              <Mic size={14} /> Mic
            </div>
            <NetQualityPill q={netQuality} />
            {chunkQueueDepth > 2 && (
              <div
                className="flex items-center gap-1.5 px-2 py-0.5 rounded-sm border text-[10px] font-mono font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border-amber-200"
                title="Recording chunks are queued for upload — keep answering, uploads are retrying in the background."
                data-testid="chunk-queue-pill"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
                Uploading · {chunkQueueDepth}
              </div>
            )}
            {/* Overall test countdown */}
            {testSecondsLeft !== null && (
              <div
                data-testid="test-countdown-pill"
                className={`flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[11px] font-mono font-black ${
                  testSecondsLeft <= 300
                    ? "bg-red-50 border-red-400 text-red-700 animate-pulse"
                    : testSecondsLeft <= 600
                    ? "bg-amber-50 border-amber-400 text-amber-700"
                    : "bg-slate-50 border-slate-300 text-slate-600"
                }`}
                title="Remaining time for this test"
              >
                <Clock size={11} />
                {testSecondsLeft > 0
                  ? `${String(Math.floor(testSecondsLeft / 60)).padStart(2,"0")}:${String(testSecondsLeft % 60).padStart(2,"0")} left`
                  : "Time up"}
              </div>
            )}
            <div className="text-xs font-mono text-slate-500">
              Q {qIndex + 1}/{questions.length}
            </div>
          </div>
        </div>
        <Progress value={progress} className="h-0.5 rounded-none" />
      </header>

      {/* Mini webcam preview — callback ref guarantees srcObject is bound
          the instant the <video> mounts, even if React remounts the DOM node
          (which was the root cause of reports "camera feed not showing"). */}
      <div className="fixed top-20 right-6 z-30 w-44 h-28 bg-black rounded-sm border border-slate-200 overflow-hidden shadow-lg">
        <video
          ref={(el) => {
            camVideoRef.current = el;
            if (el && camStreamRef.current && el.srcObject !== camStreamRef.current) {
              try {
                el.srcObject = camStreamRef.current;
                const p = el.play();
                if (p && typeof p.catch === "function") p.catch(() => {});
              } catch (e) { /* noop */ }
            }
          }}
          muted playsInline autoPlay
          className="w-full h-full object-cover"
          data-testid="webcam-preview"
        />
        <div className="absolute top-1 left-1">
          <span className="h-2 w-2 rounded-full bg-red-500 recording-pulse inline-block" />
        </div>
      </div>

      {/* Main stage */}
      <main className="pt-28 pb-32 max-w-5xl mx-auto px-6 lg:px-10">
        <Overline className="text-center">
          Question {String(qIndex + 1).padStart(2, "0")} of {String(questions.length).padStart(2, "0")}
        </Overline>
        <h1 className="font-display font-bold tracking-tight text-[#0A2540] text-3xl sm:text-4xl lg:text-5xl mt-6 text-center leading-tight" data-testid="current-question">
          {question?.text}
        </h1>

        {/* State-specific content */}
        <div className="mt-20 flex flex-col items-center">
          {phase === "listening" && (
            <>
              <Overline className={ttrLeft <= 2 ? "text-red-600" : ""}>Start speaking within</Overline>
              <div className={`font-mono font-black text-8xl mt-3 tracking-tighter ${ttrLeft <= 2 ? "timer-critical" : "text-[#0A2540]"}`} data-testid="ttr-countdown">
                {String(ttrLeft).padStart(2, "0")}
              </div>
              <div className="text-sm text-slate-500 mt-2 font-mono">seconds</div>
              <div
                className="mt-5 px-4 py-2 bg-[#2563EB] text-white rounded-sm text-sm font-bold animate-pulse"
                data-testid="speak-now-prompt"
              >
                → Speak your answer now
              </div>
              <div className="mt-8">
                <VoiceWave active={vadActive} level={vadLevel} />
              </div>
            </>
          )}
          {phase === "recording" && (
            <>
              <Overline className="text-red-600">Recording your answer</Overline>
              <div className={`font-mono font-black text-8xl mt-3 tracking-tighter ${answerLeft <= 5 ? "timer-critical" : "text-[#0A2540]"}`} data-testid="answer-countdown">
                {String(Math.max(0, answerLeft)).padStart(2, "0")}
              </div>
              <div className="text-sm text-slate-500 mt-2 font-mono">seconds remaining</div>
              <div className="mt-8">
                <VoiceWave active={vadActive} level={vadLevel} />
              </div>
              <Button
                onClick={() => finishAnswer(false)}
                variant="outline"
                className="mt-10 rounded-sm border-[#0A2540] text-[#0A2540]"
                data-testid="finish-answer-btn"
              >
                <CheckCircle2 size={14} className="mr-1" /> Finish Answer
              </Button>
              <Button
                variant="ghost"
                onClick={() => skipQuestion("Skipped by candidate")}
                className="mt-16 text-slate-400 hover:text-slate-600"
                data-testid="skip-answer-btn"
              >
                <SkipForward size={14} className="mr-1" /> Skip question
              </Button>
              <p className="text-[11px] text-slate-400 font-mono mt-1 text-center max-w-xs mx-auto">
                Note: If you skip this question, you will not be awarded any marks for it.
              </p>
            </>
          )}
          {phase === "saving" && (
            <div className="text-center" data-testid="saving-state">
              <div className="font-mono text-slate-500 animate-pulse">Saving & uploading recordings… this can take up to a minute.</div>
              <div className="text-xs text-slate-400 mt-2 font-mono">Do not close or refresh this tab.</div>
            </div>
          )}
          {phase === "submit_failed" && (
            <div className="text-center max-w-md" data-testid="submit-failed-state">
              <AlertTriangle size={48} className="text-amber-500 mx-auto" />
              <div className="font-display font-bold text-[#0A2540] text-2xl mt-4">
                Submission didn't go through
              </div>
              <p className="text-slate-600 mt-3 text-sm">
                Your answers are stored on your device. Please check your internet connection,
                then tap Retry. Do <b>NOT</b> close this tab.
              </p>
              <Button
                onClick={retrySubmit}
                className="mt-6 bg-[#0A2540] text-white rounded-sm px-8 py-5 text-base font-bold"
                data-testid="retry-submit-btn"
              >
                <CheckCircle2 size={16} className="mr-2" /> Retry Submission
              </Button>
            </div>
          )}
          {phase === "done" && (
            <div className="text-center">
              <CheckCircle2 size={48} className="text-green-600 mx-auto" />
              <div className="font-display font-bold text-[#0A2540] text-2xl mt-4">Submitted successfully</div>
            </div>
          )}
        </div>
      </main>

      {/* Mic-health warning — blocks user attention when the mic has
          gone silent (common with Bluetooth earpod disconnects mid-test) */}
      {micWarning && (
        <div
          className="fixed top-14 inset-x-0 bg-red-600 text-white z-30 shadow-lg"
          data-testid="mic-warning-banner"
        >
          <div className="max-w-[1400px] mx-auto px-6 py-2.5 flex items-center gap-3">
            <AlertTriangle size={18} className="shrink-0" />
            <div className="text-sm font-bold leading-snug flex-1">{micWarning}</div>
            <Button
              size="sm" variant="ghost"
              onClick={() => setMicWarning(null)}
              className="text-white hover:bg-white/20 rounded-sm text-xs"
              data-testid="mic-warning-dismiss"
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}

      {/* Flags footer */}
      {flags.length > 0 && (
        <footer className="fixed bottom-0 inset-x-0 bg-red-50 border-t border-red-200 z-20">
          <div className="max-w-[1400px] mx-auto px-6 py-2 flex items-center gap-3 text-xs">
            <AlertTriangle size={14} className="text-red-600" />
            <span className="font-mono font-bold text-red-700">{flags.length} proctoring flag(s)</span>
            <span className="text-red-600 truncate font-mono">Latest: {flags[flags.length - 1]?.type} — {flags[flags.length - 1]?.details}</span>
          </div>
        </footer>
      )}
    </div>
  );
}

// Visual enter-exam step with an arrow marker. Makes the share-dialog
// flow obvious so candidates don't get stuck on Chrome's greyed-out
// "Share" button (they have to click the thumbnail preview first).
function StartStep({ n, text }) {
  return (
    <li className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-sm p-3">
      <div className="h-7 w-7 shrink-0 rounded-sm bg-[#0A2540] text-white font-mono font-black text-xs flex items-center justify-center">
        {n}
      </div>
      <div className="flex-1 text-sm text-slate-700 leading-relaxed">{text}</div>
    </li>
  );
}

// Small HUD pill showing live connection quality. Green / amber / red +
// ping ms. Tooltip surfaces downlink + effectiveType when available.
function NetQualityPill({ q }) {
  const quality = q?.quality || "good";
  const ping = q?.ping_ms;
  const palette = {
    good: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500", label: "Good" },
    fair: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", dot: "bg-amber-500", label: "Fair" },
    poor: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", dot: "bg-red-500", label: "Poor" },
  }[quality] || { bg: "bg-slate-50", text: "text-slate-600", border: "border-slate-200", dot: "bg-slate-400", label: "—" };
  const tooltip = `${palette.label} · ${ping != null ? `${ping}ms` : "measuring…"}` +
    (q?.downlink_mbps != null ? ` · ${q.downlink_mbps}Mbps` : "") +
    (q?.effective_type ? ` · ${q.effective_type}` : "");
  const Icon = quality === "poor" ? WifiOff : Wifi;
  return (
    <div
      className={`flex items-center gap-1.5 px-2 py-0.5 rounded-sm border text-[10px] font-mono font-bold uppercase tracking-wider ${palette.bg} ${palette.text} ${palette.border}`}
      title={tooltip}
      data-testid="net-quality-pill"
    >
      <span className={`h-1.5 w-1.5 rounded-full ${palette.dot}`} />
      <Icon size={11} />
      <span>{palette.label}</span>
      {ping != null && <span className="opacity-70">· {ping}ms</span>}
    </div>
  );
}
